import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAfter, sub } from 'date-fns';
import useBuyerLendingRequests from '@/enterprise/data/useBuyerLendingRequests';
import useDismissibles from '@/enterprise/data/useDismissibles';
import useRegionBySrm, { UserRegion } from '@/enterprise/data/useRegionBySrm';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import useEnableLendingConnections from '@/enterprise/features/lendingConnections/useEnableLendingConnections';
import useCurrencies from '@/shared/data/useCurrencies';
import useAccountFeatureConfigs from '@/shared/providers/useAccountFeatureConfigs';
import { ContactFormConfig, GoalsFormConfig } from './modal/types';

export const useIsLendingEligible = () => {
  const region = useRegionBySrm();
  const currencies = useCurrencies();
  const { i18n } = useTranslation();

  return region === UserRegion.AMER && currencies.includes('USD') && i18n.language.startsWith('en');
};

/*
 * When show below Homepage Early Payment Markets header:
 * - No taker markets
 * - No available AP
 * - Market type view
 *
 * When show in Buyer Cards Customer View:
 * - Has taker markets and available AP
 */
export const useShowLendingInlineAd = (location: 'homepage' | 'buyers') => {
  const enableLendingConnectionsIntegration = useEnableLendingConnections();
  const isLendingEligible = useIsLendingEligible();
  const { pathname } = useLocation();
  const { wasSmbMigrated } = useAccountFeatureConfigs();

  const { data: dismissibles } = useDismissibles({ enabled: enableLendingConnectionsIntegration });
  const hasDismissedLendingInlineAd = dismissibles.find(
    (d) => d.kind === 'LENDING_INLINE_AD' && isAfter(d.dismissedAt, sub(new Date(), { months: 1 }))
  );

  const { data: buyerLendingRequests } = useBuyerLendingRequests({ enabled: enableLendingConnectionsIntegration });
  const hasBuyerLendingRequest = buyerLendingRequests.find(
    (req) => req.opportunityId && req.opportunityChildId && req.status !== 'COMPLETED' && req.appointmentScheduled
  );

  const { data: takerMarkets } = useTakerMarkets();
  const hasTakerMarkets = takerMarkets?.length > 0;
  const hasAvailableAP = takerMarkets?.some((tm) => tm.totalInvoiceAmount > 0);

  const inlineAdActive =
    !wasSmbMigrated &&
    enableLendingConnectionsIntegration &&
    isLendingEligible &&
    // If dismissed lending ad found with dismiss date within last month, do not show ad
    !hasDismissedLendingInlineAd &&
    // If user has scheduled a lending informational call and the Salesforce Opportunity has not been closed, do not show ad
    !hasBuyerLendingRequest;

  // Inline ad mixed into buyer cards
  if (location === 'buyers' && pathname.includes('customers') && hasTakerMarkets && hasAvailableAP) {
    return inlineAdActive;
  }

  // Inline ad above directly underneath Early Payment Markets header
  if (location === 'homepage' && (pathname.includes('markets') || !hasTakerMarkets || !hasAvailableAP)) {
    return inlineAdActive;
  }

  return false;
};

export const validateBuyerLendingRequest = (contact: ContactFormConfig, goals: GoalsFormConfig) => {
  return (
    !!contact.company &&
    !!contact.annualRevenue &&
    !!contact.customers &&
    !!goals.capitalAmount &&
    !!goals.products &&
    !!goals.priorities &&
    goals.existingFinancing !== undefined
  );
};
